/**
 * 经办人工作台（项目部经办人）
 *
 * 职责（文档 4.1）：上传发票、关联合同、处理退回。
 * 页面围绕这三件事组织：我的发票列表 → 关联合同 / 提交校验 → 处理被退回的发票。
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  PaperClipOutlined,
  PlusOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  InvoiceStatus,
  InvoiceTypeCode,
  PermissionCode,
  RISK_LEVEL_LABELS,
  RiskLevel,
  type Contract,
  type InvoiceListItem,
} from '@ilc/shared';
import { contractApi, invoiceApi } from '@/api/endpoints';
import { mockContractList, mockInvoiceList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { performAction } from '@/utils/performAction';
import { exportCsv } from '@/utils/export';
import { feedback } from '@/utils/feedback';
import { DataTable, BatchBar } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import {
  buildInvoiceColumns,
  buildInvoiceExportColumns,
  buildInvoiceReportColumns,
  buildInvoiceReportRows,
} from '@/components/invoiceColumns';
import { InvoiceAttachments } from '@/components/InvoiceAttachments';
import { InvoiceProjectModal } from '@/components/InvoiceProjectModal';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { useAuthStore } from '@/stores/auth';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  /** 按工程名称筛选。与 keyword 是两个独立的框，见 shared/api.ts 的说明 */
  projectName?: string;
  status?: InvoiceStatus;
  riskLevel?: RiskLevel;
  invoiceType?: InvoiceTypeCode;
  dateFrom?: string;
  dateTo?: string;
}

export default function HandlerWorkbench(): React.ReactElement {
  const navigate = useNavigate();
  const auth = useAuthStore((s) => s.auth);
  const can = useAuthStore((s) => s.can);

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [contractModal, setContractModal] = useState<InvoiceListItem | null>(null);
  const [attachmentModal, setAttachmentModal] = useState<{ id: string; bizNo: string } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [projectDraft, setProjectDraft] = useState('');
  /** 日期范围也要受控——否则「重置」清掉了查询条件，输入框里还留着旧日期 */
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [exportingReport, setExportingReport] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [projectModal, setProjectModal] = useState<{
    id: string;
    bizNo: string;
    contractId: string | null;
    projectName: string | null;
  } | null>(null);

  const list = usePagedList<InvoiceListItem, Query>({
    loader: (q) => invoiceApi.list(q),
    fallback: (q) => mockInvoiceList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  /**
   * 「重置」必须把**输入框的状态**也一起清掉。
   *
   * 从前只调 list.resetQuery()：查询条件确实清了，但关键词框、日期框里还留着
   * 旧值——用户看着框里有字，以为还在筛选，实际上结果已经是全量了。
   * 这类"界面与真实条件不一致"的坑不值得留第二次，加新搜索框时一并修掉。
   */
  const handleReset = (): void => {
    setKeywordDraft('');
    setProjectDraft('');
    setDateRange(null);
    list.resetQuery();
  };

  /** 关联合同弹窗里的合同检索 */
  const contracts = useResource(() => contractApi.list({ page: 1, pageSize: 50 }), {
    fallback: () => mockContractList({ page: 1, pageSize: 50 }),
    enabled: contractModal !== null,
  });

  const handleSubmitOne = async (record: InvoiceListItem): Promise<void> => {
    await performAction({
      action: () => invoiceApi.submit(record.id, {}),
      successMessage: `已提交校验：${record.bizNo}`,
      offline: () => list.reload(),
    });
  };

  const handleBatchSubmit = async (): Promise<void> => {
    const targets = list.list.filter(
      (i) => selectedKeys.includes(i.id) && i.invoiceStatus === InvoiceStatus.DRAFT,
    );
    if (targets.length === 0) {
      feedback.warning('所选发票中没有「待提交」状态的记录，无法提交校验');
      return;
    }
    setSubmitting(true);
    let okCount = 0;
    for (const target of targets) {
      const result = await performAction({
        action: () => invoiceApi.submit(target.id, {}),
        successMessage: `已提交校验：${target.bizNo}`,
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，批量提交仅在本地演示',
      });
      if (result.ok) okCount += 1;
    }
    setSubmitting(false);
    setSelectedKeys([]);
    if (okCount > 0) list.reload();
  };

  /**
   * 批量删除所选发票（连同复印件附件与原件文件）。
   *
   * 【为什么只删未过审的】过审之后发票已进入账务链路——校验报告、合同额度占用、
   * T+ 推送记录都挂在它身上，删了会让那些东西变成孤儿。这与权限码
   * `invoice:delete` 的说明（「删除未过审的发票」）以及服务端护栏一致。
   * 锁定的那些跳过并如实告知，而不是静默少删几个。
   *
   * `reason` 由确认弹窗强制填写，逐张随请求发给服务端写进操作日志——
   * 只在界面上收集、不落到日志里，等于没有理由。
   */
  const handleBatchDelete = async (reason: string): Promise<void> => {
    const targets = list.list.filter((i) => selectedKeys.includes(i.id));
    const deletable = targets.filter((i) => !LOCKED_FOR_EDIT.includes(i.invoiceStatus));
    const locked = targets.length - deletable.length;

    if (deletable.length === 0) {
      feedback.warning('所选发票都已过审，不能删除。如确需作废，请让财务走退回流程');
      setDeleteOpen(false);
      return;
    }

    setDeleting(true);
    let okCount = 0;
    /** 因被其它记录共用（内容哈希相同）而保留的文件数——必须如实回报，
     *  否则弹窗里那句"原件会一并删除"在去重场景下就是假话 */
    let keptFiles = 0;
    for (const target of deletable) {
      const result = await performAction({
        action: () => invoiceApi.remove(target.id, { reason }),
        successMessage: '', // 批量逐条弹提示会刷屏，最后统一给一次
      });
      if (result.ok) {
        okCount += 1;
        keptFiles += result.data?.keptFiles ?? 0;
      }
    }
    setDeleting(false);
    setDeleteOpen(false);
    setSelectedKeys([]);

    if (okCount > 0) {
      feedback.success(
        `已删除 ${okCount} 张发票及其复印件附件` +
          (keptFiles > 0 ? `；${keptFiles} 个文件因还被其它记录共用而保留` : '') +
          (locked > 0 ? `；${locked} 张因已过审被跳过` : ''),
      );
    }
    if (okCount === 0) feedback.error('删除失败，请查看上方错误提示');
    if (okCount > 0 || locked > 0) list.reload();
  };

  const handleLinkContract = async (contract: Contract): Promise<void> => {
    if (!contractModal) return;
    await performAction({
      /**
       * 【走独立的关联端点，不要改回 submit】
       * submit 是"提交校验"，状态护栏只放行 DRAFT/REJECTED——借它做关联的话，
       * 发票一旦进过流程就会报「不能重复提交」，而这正是财务初审时
       * 看到「未关联合同」后最想补的一步。
       */
      action: () => invoiceApi.setContract(contractModal.id, contract.contractNo),
      successMessage: `已关联合同 ${contract.contractNo}`,
      offline: () => {
        list.setList((prev) =>
          prev.map((item) =>
            item.id === contractModal.id
              ? { ...item, contractNo: contract.contractNo, contractId: contract.id }
              : item,
          ),
        );
      },
    }).then((result) => {
      /**
       * 【成功后必须重载列表】
       * 从前这里只弹提示、不刷新，于是列表那一行仍然显示「未关联」——
       * 用户看到"提示说关联成功、列表说没关联"，只能手动刷页面。
       * （上面的 `offline` 分支是本地改列表，只覆盖接口未就绪的情况。）
       */
      if (result.ok) list.reload();
    });
    setContractModal(null);
  };

  /**
   * 导出「专用报表」。
   *
   * 【与「导出本页」的区别】那个导的是当前页 20 条、所见即所得；
   * 报表是交给财务与审计的成品，**覆盖当前筛选条件下的全部发票**，
   * 末尾带合计行——翻页才能导全必然漏，而漏掉的数字看起来仍像"全量"。
   */
  const handleExportReport = async (): Promise<void> => {
    setExportingReport(true);
    try {
      const { rows, truncated, total } = await invoiceApi.listAll(list.query as Query);
      if (rows.length === 0) {
        feedback.warning('当前筛选条件下没有发票，无需导出');
        return;
      }
      if (truncated) {
        // 不静默截断：截断的报表比报错危险——数字看着是"全量"，实际少了一截
        feedback.warning(
          `发票共 ${total} 张，超过单次导出上限，本次只导出前 ${rows.length} 张。请缩小筛选范围后重试。`,
        );
      }
      exportCsv('发票专用报表', buildInvoiceReportColumns(), buildInvoiceReportRows(rows));
    } catch (err) {
      feedback.error(err instanceof Error ? err.message : '导出失败');
    } finally {
      setExportingReport(false);
    }
  };

  /**
   * 已过审的发票不再允许变更业务归属（合同 / 工程）。
   *
   * 与后端 `linkContract` / `setInvoiceProject` 的护栏保持一致——
   * 不这样做的话，按钮照常可点、点下去必然 409，用户只能靠报错才知道做不了。
   * 按本项目既有风格（高风险票的「推送 T+」就是这么处理的）：
   * **不禁用得莫名其妙，而是禁用 + 说明为什么、以及该走哪条路**。
   */
  const LOCKED_FOR_EDIT: InvoiceStatus[] = [
    InvoiceStatus.APPROVED,
    InvoiceStatus.PUSHED,
    InvoiceStatus.BOOKED,
    InvoiceStatus.ARCHIVED,
  ];
  const lockedHint = (record: InvoiceListItem): string | null =>
    LOCKED_FOR_EDIT.includes(record.invoiceStatus)
      ? `发票已进入「${INVOICE_STATUS_LABELS[record.invoiceStatus] ?? record.invoiceStatus}」阶段，不能再变更归属；如确需更正，请让财务走退回流程`
      : null;

  const columns = buildInvoiceColumns({
    showHandler: false,
    showSellerTaxNo: true,
    actions: (record) => (
      <>
        <Button type="link" size="small" onClick={() => navigate(`/business/invoices/${record.id}`)}>
          详情
        </Button>
        {record.invoiceStatus === InvoiceStatus.DRAFT && can(PermissionCode.INVOICE_SUBMIT) ? (
          <Button type="link" size="small" onClick={() => void handleSubmitOne(record)}>
            提交校验
          </Button>
        ) : null}
        {record.invoiceStatus === InvoiceStatus.REJECTED ? (
          <Tooltip title="查看退回原因并修改后重新提交">
            <Button
              type="link"
              size="small"
              danger
              icon={<RollbackOutlined />}
              onClick={() => navigate(`/business/invoices/${record.id}`)}
            >
              处理退回
            </Button>
          </Tooltip>
        ) : null}
        {!record.contractNo && can(PermissionCode.INVOICE_SUBMIT) ? (
          <Tooltip title={lockedHint(record) ?? '未关联合同的发票属于低风险提示项，不阻断流程'}>
            <Button
              type="link"
              size="small"
              disabled={lockedHint(record) !== null}
              onClick={() => setContractModal(record)}
            >
              关联合同
            </Button>
          </Tooltip>
        ) : null}
        {can(PermissionCode.INVOICE_READ) ? (
          <Tooltip
            title={
              lockedHint(record) ?? '把这张发票归到某个工程下，便于按工程归集成本与检索'
            }
          >
            <Button
              type="link"
              size="small"
              disabled={lockedHint(record) !== null}
              onClick={() =>
                setProjectModal({
                  id: record.id,
                  bizNo: record.bizNo,
                  contractId: record.contractId ?? null,
                  projectName: record.projectName ?? null,
                })
              }
            >
              {record.projectName ? '工程名称' : '关联工程'}
            </Button>
          </Tooltip>
        ) : null}
        {can(PermissionCode.INVOICE_ATTACHMENT_READ) ? (
          <Tooltip title="上传纸质票的复印件，并提取票面上的手写签字/批注">
            <Button
              type="link"
              size="small"
              icon={<PaperClipOutlined />}
              onClick={() => setAttachmentModal({ id: record.id, bizNo: record.bizNo })}
            >
              上传附件
            </Button>
          </Tooltip>
        ) : null}
      </>
    ),
  });

  const rejected = list.list.filter((i) => i.invoiceStatus === InvoiceStatus.REJECTED);

  /** 删除确认弹窗要如实说清"删几张、跳过几张"，不能只说一句笼统的提示 */
  const deleteTargets = list.list.filter((i) => selectedKeys.includes(i.id));
  const deletableTargets = deleteTargets.filter((i) => !LOCKED_FOR_EDIT.includes(i.invoiceStatus));
  const lockedTargets = deleteTargets.length - deletableTargets.length;

  return (
    <div className="ilc-page">
      <PageHeader
        title="我的发票"
        description={`经办人：上传发票、关联合同、处理退回。数据权限「${auth?.deptId ? '本部门' : '本租户'}」内的发票均可查看。`}
        extra={
          <Space>
            <Button icon={<CloudUploadOutlined />} onClick={() => navigate('/business/upload')}>
              上传发票
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/business/upload')}
              disabled={!can(PermissionCode.INVOICE_CREATE)}
            >
              新建上传
            </Button>
          </Space>
        }
      />

      {rejected.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`有 ${rejected.length} 张发票被财务退回，请查看退回原因并处理后重新提交`}
          description={
            <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
              {rejected.slice(0, 3).map((item) => (
                <li key={item.id}>
                  <Space>
                    <Link to={`/business/invoices/${item.id}`}>{item.bizNo}</Link>
                    <Typography.Text type="secondary" ellipsis style={{ maxWidth: 520 }}>
                      {item.lastRejectReason ?? '未填写退回原因'}
                    </Typography.Text>
                  </Space>
                </li>
              ))}
            </ul>
          }
        />
      ) : null}

      <DataTable<InvoiceListItem>
        storageKey="handler-invoices"
        exportFileName="我的发票"
        exportColumns={buildInvoiceExportColumns()}
        exportExtra={
          <Tooltip title="按当前筛选条件导出**全部**发票（不止当前页），含合计行；备注取票面备注">
            <Button
              icon={<FileExcelOutlined />}
              loading={exportingReport}
              onClick={() => void handleExportReport()}
            >
              导出专用报表
            </Button>
          </Tooltip>
        }
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="还没有上传发票，点击右上角「上传发票」开始"
        emptyAction={
          <Button type="primary" onClick={() => navigate('/business/upload')}>
            去上传
          </Button>
        }
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys) => setSelectedKeys(keys),
        }}
        footer={() =>
          selectedKeys.length > 0 ? (
            <BatchBar count={selectedKeys.length} onClear={() => setSelectedKeys([])}>
              <Button size="small" type="primary" icon={<SendOutlined />} loading={submitting} onClick={() => void handleBatchSubmit()}>
                批量提交校验
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                仅「待提交」状态的发票会被提交
              </Typography.Text>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={!can(PermissionCode.INVOICE_DELETE)}
                onClick={() => setDeleteOpen(true)}
              >
                删除
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                仅「未过审」的发票会被删除
              </Typography.Text>
            </BatchBar>
          ) : null
        }
        toolbar={
          <Space wrap>
            {/* 按工程名称放在最前：财务归集成本时最先想到的就是"这是哪个工程的" */}
            <Input.Search
              placeholder="按工程名称"
              allowClear
              style={{ width: 200 }}
              value={projectDraft}
              onChange={(e) => setProjectDraft(e.target.value)}
              onSearch={(value) => list.setQuery({ projectName: value || undefined })}
            />
            <Input.Search
              placeholder="内部单号 / 发票号码 / 销方名称"
              allowClear
              style={{ width: 260 }}
              value={keywordDraft}
              onChange={(e) => setKeywordDraft(e.target.value)}
              onSearch={(value) => list.setQuery({ keyword: value || undefined })}
            />
            <Select
              placeholder="发票状态"
              allowClear
              style={{ width: 130 }}
              value={list.query.status}
              onChange={(value) => list.setQuery({ status: value })}
              options={Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              placeholder="风险等级"
              allowClear
              style={{ width: 120 }}
              value={list.query.riskLevel}
              onChange={(value) => list.setQuery({ riskLevel: value })}
              options={Object.entries(RISK_LEVEL_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              placeholder="票种"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 200 }}
              value={list.query.invoiceType}
              onChange={(value) => list.setQuery({ invoiceType: value })}
              options={Object.entries(INVOICE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <DatePicker.RangePicker
              style={{ width: 240 }}
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                list.setQuery({
                  dateFrom: range?.[0] ? dayjs(range[0]).format('YYYYMMDD') : undefined,
                  dateTo: range?.[1] ? dayjs(range[1]).format('YYYYMMDD') : undefined,
                });
              }}
            />
            <Button onClick={handleReset}>重置</Button>
          </Space>
        }
      />

      <Modal
        open={contractModal !== null}
        title={`关联合同 —— ${contractModal?.bizNo ?? ''}`}
        onCancel={() => setContractModal(null)}
        footer={null}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="未关联合同的发票属于低风险提示项，不阻断流程；但关联后校验报告才能做合同金额与品类的比对，审计追溯也更完整。"
        />
        {contracts.loading ? (
          <Typography.Text type="secondary">合同加载中…</Typography.Text>
        ) : (
          <Form layout="vertical">
            <Form.Item label="选择合同">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="按合同编号 / 名称 / 供应商搜索"
                loading={contracts.loading}
                options={(contracts.data?.list ?? []).map((c) => ({
                  value: c.id,
                  label: `${c.contractNo} · ${c.name} · ${c.supplierName}`,
                }))}
                onChange={(value) => {
                  const contract = (contracts.data?.list ?? []).find((c) => c.id === value);
                  if (contract) void handleLinkContract(contract);
                }}
              />
            </Form.Item>
            {(contracts.data?.list ?? []).length === 0 ? (
              <Alert
                type="warning"
                showIcon
                message="合同列表为空"
                description="合同接口未就绪或该租户尚未维护合同，可先联系法务在「合同与抽查」中录入合同。"
              />
            ) : null}
            <Space>
              <Tag>{INVOICE_TYPE_LABELS[contractModal?.invoiceType ?? InvoiceTypeCode.UNKNOWN]}</Tag>
              <Typography.Text type="secondary">
                发票金额 ¥{(((contractModal?.totalCents ?? 0) / 100)).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </Typography.Text>
            </Space>
          </Form>
        )}
      </Modal>

      <InvoiceAttachments invoice={attachmentModal} onClose={() => setAttachmentModal(null)} />

      <ConfirmDanger
        open={deleteOpen}
        level="high"
        title={`删除所选 ${deleteTargets.length} 张发票？`}
        description={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <span>
              将删除 <strong>{deletableTargets.length}</strong> 张发票，并
              <strong>一并删除</strong>它们名下的复印件附件与发票原件文件。
            </span>
            {lockedTargets > 0 ? (
              <span style={{ color: '#d46b08' }}>
                另有 {lockedTargets} 张已过审，会被跳过——已进入账务链路的发票不能删除，
                如确需作废请让财务走退回流程。
              </span>
            ) : null}
            <span>
              删除是<strong>真的删文件</strong>，不是只删记录：存储里没有垃圾回收，留着就
              再也没有线索指向它们了。确认后无法恢复。
            </span>
            <span style={{ color: '#8c8c8c' }}>
              若某个文件还被别的发票/合同共用（同一份文件重复上传会命中同一份存储），
              系统会保留它，并在删除结果里告诉你留了几个。
            </span>
          </Space>
        }
        acknowledgeText="我已知晓：删除后无法恢复，且关联的复印件附件与原件文件会一并删除"
        reasonLabel="删除原因（必填，将写入操作日志）"
        reasonPresets={['重复上传，删除多余记录', '测试数据，清理掉', '录错了，重新上传']}
        confirmText="确认删除"
        loading={deleting}
        onConfirm={handleBatchDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      <InvoiceProjectModal
        invoice={projectModal}
        currentProjectName={projectModal?.projectName ?? null}
        onClose={() => setProjectModal(null)}
        onSaved={() => list.reload()}
      />
    </div>
  );
}
